// iter426 — Auto-Reconcile settings dialog.
// Tiny settings surface for the scheduled-auto-reconcile cron added in
// iter426. User picks: enabled, schedule (daily/weekly), confidence
// threshold (0-100, 95 = account-only), period_days, direction, optional
// email_to. Also shows "Last run: X groups, Y txns" and a Run-Now button.
import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Play, Save, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const fmtSR = (n) => `${cur()} ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export function AutoReconcileSettingsDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [settings, setSettings] = useState(null);
  const [runs, setRuns] = useState([]);
  // iter427 — Promotion candidates: payees auto-applied ≥ 3× without
  // a corresponding banking rule yet. Click "Remember this" to convert.
  const [candidates, setCandidates] = useState([]);
  const [promoting, setPromoting] = useState(null); // payee_id being promoted

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, r, c] = await Promise.all([
        api.get('/scheduler/auto-reconcile/settings'),
        api.get('/scheduler/auto-reconcile/runs?limit=5'),
        api.get('/scheduler/auto-reconcile/promotion-candidates?min_runs=3'),
      ]);
      setSettings(s.data);
      setRuns(r.data?.runs || []);
      setCandidates((c.data?.candidates || []).filter((x) => !x.has_existing_rule));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const promoteCandidate = async (cand) => {
    setPromoting(cand.payee_id);
    try {
      const r = await api.post('/scheduler/auto-reconcile/promote-rule', {
        payee_type: cand.payee_type,
        payee_id: cand.payee_id,
        match_value: cand.suggested_match_value,
      });
      toast.success(`Rule "${r.data?.name}" created`);
      // Drop the promoted row locally so the user sees instant feedback.
      setCandidates((prev) => prev.filter((x) => x.payee_id !== cand.payee_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Promote failed');
    } finally {
      setPromoting(null);
    }
  };

  useEffect(() => {
    if (open) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const update = (k, v) => setSettings((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const r = await api.put('/scheduler/auto-reconcile/settings', {
        enabled: settings.enabled,
        schedule: settings.schedule,
        confidence_threshold: Number(settings.confidence_threshold) || 95,
        period_days: Number(settings.period_days) || 30,
        direction: settings.direction,
        email_to: settings.email_to || null,
        notify_inapp: settings.notify_inapp,
      });
      setSettings(r.data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post('/scheduler/auto-reconcile/run-now');
      const d = r.data || {};
      if (d.skipped) {
        toast.info(`Run skipped: ${d.reason}`);
      } else {
        toast.success(`Linked ${d.txns_linked || 0} txn${d.txns_linked === 1 ? '' : 's'} totalling ${fmtSR(d.total_amount || 0)}`);
      }
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  if (loading || !settings) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          {/* iter426 — Hidden title for screen readers (Radix logs an
              a11y warning when DialogContent has no DialogTitle). */}
          <DialogHeader className="sr-only">
            <DialogTitle>Loading auto-reconcile settings</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <Loader2 size={20} className="animate-spin text-muted-foreground" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="auto-recon-settings-dialog">
        <DialogHeader>
          <DialogTitle>Scheduled Auto-Reconcile</DialogTitle>
          <DialogDescription>
            Run the same Reconcile-Month scan automatically and auto-apply any group
            whose confidence is at or above your threshold. Skipped groups land in your
            in-app digest for review.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center justify-between border rounded-md p-2">
            <div>
              <Label className="text-sm font-semibold">Enabled</Label>
              <p className="text-[11px] text-muted-foreground">When off, the cron skips this tenant entirely.</p>
            </div>
            <Switch
              checked={!!settings.enabled}
              onCheckedChange={(v) => update('enabled', v)}
              data-testid="auto-recon-enabled"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Schedule</Label>
              <Select value={settings.schedule} onValueChange={(v) => update('schedule', v)}>
                <SelectTrigger className="mt-1" data-testid="auto-recon-schedule"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Direction</Label>
              <Select value={settings.direction} onValueChange={(v) => update('direction', v)}>
                <SelectTrigger className="mt-1" data-testid="auto-recon-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="outgoing">Outgoing only</SelectItem>
                  <SelectItem value="incoming">Incoming only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Confidence threshold (%)</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={settings.confidence_threshold}
                onChange={(e) => update('confidence_threshold', e.target.value)}
                className="mt-1"
                data-testid="auto-recon-threshold"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">
                95 = account-number matches only (safest). 80 = also includes name-match groups.
              </p>
            </div>
            <div>
              <Label className="text-xs">Lookback period (days)</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={settings.period_days}
                onChange={(e) => update('period_days', e.target.value)}
                className="mt-1"
                data-testid="auto-recon-period"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">Email digest (optional)</Label>
            <Input
              type="email"
              value={settings.email_to || ''}
              onChange={(e) => update('email_to', e.target.value)}
              placeholder="finance@yourco.com"
              className="mt-1"
              data-testid="auto-recon-email"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Leave blank to skip emails. The in-app notification always fires when something happens.
            </p>
          </div>

          <div className="flex items-center justify-between border rounded-md p-2">
            <div>
              <Label className="text-sm font-semibold">In-app notification</Label>
              <p className="text-[11px] text-muted-foreground">Toast + bell icon entry on each run.</p>
            </div>
            <Switch
              checked={settings.notify_inapp !== false}
              onCheckedChange={(v) => update('notify_inapp', v)}
              data-testid="auto-recon-notify-inapp"
            />
          </div>

          {/* iter427 — Recurring-group promotion candidates */}
          {candidates.length > 0 && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-2" data-testid="auto-recon-promotion-candidates">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-800 mb-1.5 flex items-center gap-1">
                <span aria-hidden>⭐</span> Remember these payees? ({candidates.length})
              </div>
              <p className="text-[10px] text-emerald-700/90 mb-2">
                Auto-Reconcile linked these <b>3+ times</b>. Promote to a permanent banking rule and the next 1,000 txns
                land instantly without waiting for the cron.
              </p>
              <div className="space-y-1">
                {candidates.slice(0, 5).map((c) => (
                  <div key={`${c.payee_type}:${c.payee_id}`} className="flex items-center gap-2 text-[11px] bg-white border border-emerald-200 rounded px-2 py-1">
                    <Badge className="text-[9px] uppercase">{c.payee_type}</Badge>
                    <span className="font-semibold truncate flex-1" title={c.payee_name}>{c.payee_name}</span>
                    <span className="text-emerald-700 tabular-nums whitespace-nowrap">{c.success_count}× · {c.total_txns_linked} txns</span>
                    <Button
                      size="sm"
                      className="h-6 px-2 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => promoteCandidate(c)}
                      disabled={promoting === c.payee_id}
                      data-testid={`promote-rule-${c.payee_id}`}
                    >
                      {promoting === c.payee_id ? <Loader2 size={10} className="animate-spin" /> : 'Remember this'}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent runs */}
          {runs.length > 0 && (
            <div className="border rounded-md p-2 bg-muted/40">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1 flex items-center gap-1">
                <Calendar size={10} /> Last {runs.length} run{runs.length > 1 ? 's' : ''}
              </div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {runs.map((r) => (
                  <div key={r.id} className="flex items-baseline gap-2 text-[11px]">
                    <span className="text-muted-foreground tabular-nums">{r.started_at?.slice(0, 16).replace('T', ' ')}</span>
                    <Badge variant="outline" className="text-[9px] uppercase">{r.trigger}</Badge>
                    <span className="text-foreground/80 truncate">
                      {r.txns_linked || 0} linked · {fmtSR(r.total_amount || 0)} · {r.scanned} scanned
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {settings.next_run_at && (
            <p className="text-[10px] text-muted-foreground text-center">
              Next scheduled run: {settings.next_run_at.slice(0, 16).replace('T', ' ')} UTC
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 flex-col-reverse sm:flex-row">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="auto-recon-close">Close</Button>
          <Button
            variant="outline"
            onClick={runNow}
            disabled={running}
            data-testid="auto-recon-run-now"
          >
            {running ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Running…</> : <><Play size={14} className="mr-1.5" /> Run now</>}
          </Button>
          <Button onClick={save} disabled={saving} data-testid="auto-recon-save">
            {saving ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Saving…</> : <><Save size={14} className="mr-1.5" /> Save</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AutoReconcileSettingsDialog;
